import { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useRoster, type TabKey } from '../lib/roster'
import { suppressionLabel } from '../lib/emailrules'
import { sortDetail } from '../lib/filters'
import { badges } from '../lib/predicates'
import { fullName } from '../lib/format'
import AgentPanel from '../components/AgentPanel'
import Headshot from '../components/Headshot'

/**
 * The two-pane shape from the old dashboard: a scrolling list on the left that
 * keeps your place, detail on the right. Selecting an agent does not navigate
 * away from the list, which is the whole point.
 */
export default function Workspace({ tab }: { tab: TabKey }) {
  const r = useRoster()
  const { agents, error, tagsByAgent, visible, sortKey } = r
  const [selected, setSelected] = useState<string | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()

  const rows = visible(tab)

  // Keep a selection that is still in the list; otherwise clear it rather than
  // showing detail for someone the current filters exclude.
  useEffect(() => {
    if (selected && !rows.some((a) => a.rezen_id === selected)) setSelected(null)
  }, [rows, selected])

  /**
   * The deep-link half of `Tasks.tsx`'s `openAgent()` (and anywhere else that
   * wants to land straight on one record — `?agent=<rezen_id>` is a generic
   * mechanism, not something private to Tasks). The caller still applies a
   * view first, the same "show me" trick `Gaps.tsx` uses, so the agent is
   * guaranteed to be in `rows` regardless of whatever filters were on screen
   * before — this effect only has to wait for that agent to actually show up
   * here and then finish the job by selecting them, instead of leaving her to
   * click the one row it narrowed down to.
   *
   * Waits on `rows`, not just running once on mount: the view's filters land
   * in the roster context as a *separate* state update from the navigate that
   * changes this URL, so on the very first render after arriving, `rows` can
   * still reflect the OLD filters for one tick. Firing early would find
   * nothing, and with nothing left in the URL to fire again from, the deep
   * link would silently do nothing.
   *
   * The param is stripped the moment it's used (`replace`, so this doesn't
   * add a history entry) — otherwise it would keep re-selecting this agent
   * every time `rows` recomputes for an unrelated reason (a filter she
   * changes by hand, the roster refreshing), overriding whoever she clicks
   * into next.
   */
  useEffect(() => {
    const openId = searchParams.get('agent')
    if (!openId) return
    if (!rows.some((a) => a.rezen_id === openId)) return
    setSelected(openId)
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.delete('agent')
        return next
      },
      { replace: true },
    )
  }, [searchParams, rows, setSearchParams])

  if (error) return <div className="empty">Could not load the roster: {error}</div>
  if (!agents) return <div className="empty">Loading roster…</div>

  const current = rows.find((a) => a.rezen_id === selected) ?? null

  return (
    /* `has-selection` only changes anything below the phone breakpoint —
       see styles.css. Desktop/tablet always show both panes; a phone
       shows one at a time (list, or the open record with a Back button),
       because a 330px list column and the detail pane both trying to fit
       a phone-width screen at once is the two-pane desktop layout shown
       small, not something actually usable with a thumb. */
    <div className={`wrap${current ? ' has-selection' : ''}`}>
      <div id="list">
        <ListHead rows={rows} />
        {rows.length === 0 && <div className="hint" style={{ padding: 14 }}>No agents match.</div>}
        {rows.map((a) => {
          const tags = tagsByAgent.get(a.rezen_id) ?? []
          const detail = sortDetail(a, sortKey)
          return (
            <div
              key={a.rezen_id}
              className={`li ${a.rezen_id === selected ? 'active' : ''} ${a.is_active ? '' : 'dep'}`}
              onClick={() => setSelected(a.rezen_id)}
            >
              {/*
                The tick box builds the mailing list; clicking the row opens the
                agent. Two different meanings of "select" on one line, so the
                box stops the click from reaching the row — otherwise ticking
                somebody also swaps the detail panel, which reads as a glitch.
              */}
              <label
                className="pick"
                onClick={(e) => e.stopPropagation()}
                title={r.isSelected(a.rezen_id) ? 'On the mailing list' : 'Add to the mailing list'}
              >
                <input
                  type="checkbox"
                  checked={r.isSelected(a.rezen_id)}
                  onChange={() => r.toggleSelected(a.rezen_id)}
                  aria-label={
                    r.isSelected(a.rezen_id)
                      ? `Take ${fullName(a)} off the mailing list`
                      : `Add ${fullName(a)} to the mailing list`
                  }
                />
              </label>
              <Headshot agent={a} size="list" />
              <div style={{ minWidth: 0 }}>
                <div className="nm">
                  {fullName(a)}
                  {badges(a).map((b) => (
                    <span key={b.code} className={`pill ${b.code}`}>
                      {b.label}
                    </span>
                  ))}
                </div>
                <div className="tm">{a.team_name || a.region || a.primary_market || '—'}</div>
                {/* Sorting by something invisible asks you to take the order on
                    trust. Show whatever the list is ordered by. */}
                {detail && <div className="tm">{detail}</div>}
                {tags.length > 0 && (
                  <div className="litags">
                    {/*
                      Same name as the panel gives it, one pane away. These two
                      are on screen together: the list on the left said
                      "no-outreach" while the card on the right said "No
                      one-to-one emails" — the same tag, twice, under two names.

                      The filter dropdown and the search box still work on the
                      stored name, which is why the panel keeps it in view.
                    */}
                    {tags.map((t) => (
                      <span className={`tagchip${suppressionLabel(t) ? ' stop' : ''}`} key={t} title={t}>
                        {suppressionLabel(t)?.label ?? t}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>

      <div id="detail">
        {current ? (
          <>
            {/* Phone-only (hidden by default via styles.css) — the way back
                from a single open record to the list, now that the two are
                not both on screen at once at that width. */}
            <button type="button" className="btn ghost sm back-to-list" onClick={() => setSelected(null)}>
              ← Back to list
            </button>
            <AgentPanel agent={current} />
          </>
        ) : (
          <div className="empty">Select an agent</div>
        )}
      </div>
    </div>
  )
}

/**
 * The bar above the list: tick everyone on screen, and clear the whole list.
 *
 * These two actions already existed inside the Mailing list menu, and that was
 * the wrong place for them. Filtering to 58 team leaders and then wanting all
 * 58 is the commonest thing anyone will do here, and the answer was two clicks
 * into a dropdown — so it read as though the only way was 58 individual ticks.
 * An affordance for the tick boxes belongs beside the tick boxes.
 *
 * The two buttons deliberately mean different things, and both say their number
 * out loud:
 *
 *   - The tick box acts on **what is on screen** — 58 of them.
 *   - Clear acts on **the whole mailing list**, which may be far larger than
 *     what is on screen, because the list survives a filter change.
 */
function ListHead({ rows }: { rows: { rezen_id: string }[] }) {
  const r = useRoster()
  const box = useRef<HTMLInputElement>(null)

  const picked = useMemo(
    () => rows.reduce((n, a) => (r.isSelected(a.rezen_id) ? n + 1 : n), 0),
    [rows, r.selection],
  )
  const all = rows.length > 0 && picked === rows.length
  const some = picked > 0 && !all

  // `indeterminate` is not an attribute, only a property, so React cannot set
  // it from JSX. Without this a half-ticked list shows an empty box, which
  // reads as "nobody here is selected" when 3 of 58 are.
  useEffect(() => {
    if (box.current) box.current.indeterminate = some
  }, [some])

  /**
   * An empty roster still gets this bar when there is something to put back.
   *
   * It used to return null on zero rows, which is right for the tick box — there
   * is nothing on screen to tick — but it took the undo down with it, and the
   * undo is the one control that has nothing to do with what is on screen.
   *
   * That combination is reachable, not theoretical, because the composer
   * deliberately ignores the filters once a list exists. Filter to Charleston,
   * add 24, change the filters to something matching nobody, press Email 24 —
   * legitimate, the list is the audience — then Cancel. She lands on a roster
   * reading "No agents match" with her 24 gone and no Put back anywhere,
   * because the bar that holds it was not drawn. The same hole opens for a
   * frame when "Show only these" is on: the list empties, the scope is turned
   * off one commit later, and in between the roster has no rows.
   *
   * The tick box is left out in that state rather than offered against nothing.
   */
  if (rows.length === 0 && !r.undoSelection) return null

  return (
    <div className="listhead">
      {rows.length > 0 && (
        <label className="chk">
          <input
            ref={box}
            type="checkbox"
            checked={all}
            onChange={() => {
              const ids = rows.map((a) => a.rezen_id)
              if (all) r.removeFromSelection(ids)
              else r.addToSelection(ids)
            }}
          />
          {all ? `Unselect these ${rows.length}` : `Select all ${rows.length}`}
        </label>
      )}

      <span className="spacer" />

      {r.undoSelection ? (
        /*
          Says what happened, then offers the undo.

          Solid, not ghost. This is the only way back from an emptied list, and
          the list now empties on two exits she does not think of as destructive
          — Cancel in the composer, and a send that worked. Grey-on-white for
          the one control that undoes a mistake is the wrong way round, and the
          reason the whole type scale went up a step is that it was hard to
          read. It only exists when there is something to put back.

          The cause matters most in the 'sent' case. A bare "Put back 40" after
          a send is a button whose only meaning is "re-load the 40 people you
          have just emailed", and pressing it turns the toolbar back into
          Email 40 — one click from sending them the same thing twice. Saying
          so is the difference between an undo and a trap.
        */
        <>
          <span className="hint">
            {r.undoSelection.what === 'sent'
              ? `${r.undoSelection.count} just emailed`
              : r.undoSelection.what === 'removed'
                ? `${r.undoSelection.count} taken off`
                : 'list cleared'}
          </span>
          <button className="btn sm" onClick={() => r.undoSelection?.run()}>
            Put back {r.undoSelection.count}
          </button>
        </>
      ) : (
        r.selectedCount > 0 && (
          <>
            <span className="hint">{r.selectedCount} on the list</span>
            <button className="btn ghost sm" onClick={() => r.clearSelection()}>
              Clear
            </button>
          </>
        )
      )}
    </div>
  )
}
